import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import {
  Warning, CheckCircle, Info, Wind, Drop, Sun, Thermometer, Bug, Leaf,
  Cow, Basket, Plant, Sparkle,
} from "@phosphor-icons/react";

/**
 * FarmersWeatherActions — renders the Tool 6 rules-engine output.
 * Feeds off /api/farmers/weather-actions and shows plain-language action
 * cards ranked by severity. Every card exposes source + timestamp +
 * reasoning + confidence so farmers can decide, not just be told.
 */
const ICON_FOR = {
  irrigation: Drop, "spray-window": Wind, "frost-risk": Thermometer,
  "heat-stress": Sun, "hail-risk": Warning, "wind-damage": Wind,
  "soil-workability": Leaf, "pest-risk": Bug, "disease-risk": Bug,
  "livestock-shelter": Cow, "harvest-window": Basket, "planting-window": Plant,
};

const SEVERITY_META = {
  urgent:  { label: "Urgent",  cls: "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" },
  warning: { label: "Warning", cls: "bg-[color:var(--np-brand,#B85F2A)]/10 text-[color:var(--np-brand,#B85F2A)] border-[color:var(--np-brand,#B85F2A)]/25" },
  info:    { label: "Watch",   cls: "bg-[#E8DFD0] text-[#3A3830] border-black/10" },
  ok:      { label: "Good",    cls: "bg-emerald-50 text-emerald-800 border-emerald-100" },
};

export default function FarmersWeatherActions({ loc, crop }) {
  const [state, setState] = useState({ loading: true, actions: [], error: null, generatedAt: null });

  useEffect(() => {
    if (!loc?.lat || !loc?.lon) return;
    let cancel = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    const params = new URLSearchParams({ lat: String(loc.lat), lon: String(loc.lon) });
    if (crop) params.set("crop", crop);
    http.get(`/farmers/weather-actions?${params.toString()}`)
      .then((r) => {
        if (cancel) return;
        setState({
          loading: false,
          actions: r.data.actions || [],
          error: null,
          generatedAt: r.data.generated_at || null,
        });
      })
      .catch((e) => !cancel && setState({ loading: false, actions: [], error: e?.message || "Could not load actions.", generatedAt: null }));
    return () => { cancel = true; };
  }, [loc?.lat, loc?.lon, crop]);

  if (!loc?.lat) return null;

  const { loading, actions, error, generatedAt } = state;

  return (
    <section
      className="mt-6 rounded-2xl border border-black/10 bg-white p-6"
      data-testid="farmers-weather-actions"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <Sparkle size={11} weight="fill" className="inline mr-1" /> Weather-to-Action · Tool 6
          </div>
          <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">Do this today</h2>
          <p className="text-sm text-[#3A3830] mt-1">
            Twelve forecast rules, ranked by urgency. Every card shows why we're recommending it.
          </p>
        </div>
        {generatedAt && (
          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#6C685E]">
            Source · Open-Meteo · {new Date(generatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        )}
      </div>

      {loading && (
        <div className="mt-4 h-24 bg-black/5 rounded-xl animate-pulse" data-testid="farmers-weather-actions-loading" />
      )}

      {!loading && error && (
        <div className="mt-4 rounded-xl border border-[color:var(--np-brand,#B85F2A)]/25 bg-[color:var(--np-brand,#B85F2A)]/8 text-[color:var(--np-brand,#B85F2A)] p-4 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && actions.length === 0 && (
        <div className="mt-4 rounded-xl border border-black/10 bg-[#FBF2E1]/40 p-4 text-sm text-[#3A3830]" data-testid="farmers-weather-actions-empty">
          <CheckCircle size={14} weight="fill" className="inline text-emerald-600 mr-1" />
          No weather-driven actions right now. The forecast looks routine for the next week.
        </div>
      )}

      {!loading && !error && actions.length > 0 && (
        <div className="mt-4 grid md:grid-cols-2 gap-3" data-testid="farmers-weather-actions-list">
          {actions.map((a) => {
            const Icon = ICON_FOR[a.rule_id] || Info;
            const sev = SEVERITY_META[a.severity] || SEVERITY_META.info;
            return (
              <article
                key={a.rule_id}
                data-testid={`weather-action-${a.rule_id}`}
                className="rounded-2xl border border-black/10 bg-[#FBF2E1]/40 p-4 flex flex-col gap-2"
              >
                <div className="flex items-center gap-2">
                  <span className="grid place-items-center h-9 w-9 rounded-full bg-white border border-black/10">
                    <Icon size={16} weight="duotone" className="text-[color:var(--np-brand,#B85F2A)]" />
                  </span>
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase ${sev.cls}`}>
                    {sev.label}
                  </span>
                  <span className="ml-auto font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#6C685E]">
                    {a.when} · {a.confidence}%
                  </span>
                </div>
                <h3 className="font-serif-display text-lg text-[#151312] leading-tight">{a.title}</h3>
                <p className="text-sm text-[#3A3830] leading-snug">{a.reasoning}</p>
                <div className="mt-1 pt-2 border-t border-black/10 flex items-center gap-2 flex-wrap">
                  <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#6C685E]">
                    Rule · {a.rule_id}
                  </span>
                  {a.applicable_crops && a.applicable_crops[0] !== "all" && (
                    <span className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                      · {a.applicable_crops.join(" · ")}
                    </span>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
